import defaultTheme from './partners/default';
import avivaTheme from './partners/aviva';

const PARTNER_THEMES = {
  aviva: avivaTheme,
};

/** LMI baseline accents used when no partner theme is active. */
const LMI_ACCENTS = {
  primary: '#f1c400',
  navy: '#343579',
  report: '#343579',
  risk: '#a8c08f',
  program: '#5bc2e7',
  analysis: '#343579',
  design: '#f1c400',
  implementation: '#cb2c30',
  validation: '#00685e',
};

function toKebabCase(value) {
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();
}

/** Active partner id from REACT_APP_PARTNER (e.g. "aviva"), or "". */
export function getPartnerId() {
  return (process.env.REACT_APP_PARTNER || '').toLowerCase().trim();
}

export function isPartner(partnerId) {
  return getPartnerId() === String(partnerId || '').toLowerCase().trim();
}

/** Theme config for the active partner, or default LMI tokens when unset. */
export function getPartnerTheme(partnerId = getPartnerId()) {
  if (!partnerId) return defaultTheme;
  return PARTNER_THEMES[partnerId] || defaultTheme;
}

/** Partner contact mailbox when configured; otherwise ContinuityCoach support. */
export function getPartnerContactEmail(partnerId = getPartnerId()) {
  const theme = getPartnerTheme(partnerId);
  return theme.contactEmail || 'support@continuitycoach.com';
}

function isActivePartnerTheme(theme = getPartnerTheme()) {
  return Boolean(getPartnerId() && theme && theme.id && theme.id !== 'default');
}

/** Build inline `--partner-*` declarations for export HTML / print chrome. */
export function buildPartnerCssVariableDeclarations(theme = getPartnerTheme()) {
  if (!isActivePartnerTheme(theme)) return '';

  const { colors = {}, fonts = {}, radii = {} } = theme;
  const parts = [];

  Object.entries(colors).forEach(([key, value]) => {
    if (value == null || value === '') return;
    parts.push(`--partner-${toKebabCase(key)}:${value}`);
  });

  Object.entries(radii).forEach(([key, value]) => {
    if (value == null || value === '') return;
    parts.push(`--partner-radius-${toKebabCase(key)}:${value}`);
  });

  if (fonts.family) parts.push(`--partner-font-family:${fonts.family}`);
  if (fonts.displayFamily) parts.push(`--partner-font-display-family:${fonts.displayFamily}`);

  return parts.join(';');
}

/**
 * Body class + CSS variables for HTML sent to the PDF export API.
 * Without these, partner SCSS (`body.partner-aviva`) and `var(--partner-*)` never apply.
 */
export function getExportHtmlBodyAttributes(theme = getPartnerTheme()) {
  const partnerId = getPartnerId();
  const className = partnerId && isActivePartnerTheme(theme) ? `partner-${partnerId}` : '';
  const style = buildPartnerCssVariableDeclarations(theme);
  return { className, style };
}

/** Resolved hex colours for Puppeteer header/footer templates (no CSS vars). */
export function getReportExportBrandColors(theme = getPartnerTheme()) {
  if (isActivePartnerTheme(theme)) {
    return {
      navy: theme.colors?.brandNavy || LMI_ACCENTS.report,
      primary: theme.colors?.brandPrimary || LMI_ACCENTS.primary,
    };
  }
  return {
    navy: LMI_ACCENTS.report,
    primary: LMI_ACCENTS.primary,
  };
}

/**
 * Accent colour for ProcessBar / Card / InfoBox / Progress / report chrome.
 * @param {'primary'|'navy'|'report'|'risk'|'program'|'analysis'|'design'|'implementation'|'validation'} role
 */
export function getPartnerAccent(role = 'primary') {
  const theme = getPartnerTheme();
  if (!isActivePartnerTheme(theme)) {
    return LMI_ACCENTS[role] || LMI_ACCENTS.primary;
  }

  const c = theme.colors || {};
  const partnerAccents = {
    primary: c.brandPrimary,
    navy: c.brandNavy,
    report: c.brandNavy,
    risk: c.brandAccent || c.brandNavy,
    program: c.brandSecondary || c.brandNavy,
    analysis: c.brandNavy,
    design: c.brandPrimary,
    implementation: LMI_ACCENTS.implementation,
    validation: c.brandSuccess || c.brandAccent || c.brandNavy,
  };

  return partnerAccents[role] || c.brandNavy || LMI_ACCENTS.primary;
}

/**
 * Applies partner body class + CSS custom properties for white-label theming.
 * Call once at app bootstrap (index.js). No-op styling when partner is default/unset
 * beyond leaving LMI styles in place (no partner-* class).
 */
export function applyPartnerTheme() {
  if (typeof document === 'undefined') return;

  const partnerId = getPartnerId();
  const theme = getPartnerTheme(partnerId);
  if (!partnerId || !isActivePartnerTheme(theme)) return;

  document.body.classList.add(`partner-${partnerId}`);

  const declarations = buildPartnerCssVariableDeclarations(theme);
  if (!declarations) return;

  declarations.split(';').forEach((pair) => {
    const idx = pair.indexOf(':');
    if (idx <= 0) return;
    const name = pair.slice(0, idx).trim();
    const value = pair.slice(idx + 1).trim();
    if (name && value) document.body.style.setProperty(name, value);
  });
}

export { defaultTheme, avivaTheme, PARTNER_THEMES, LMI_ACCENTS };
